import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCameraRecovery } from '@renderer/pages/View'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { COUNTDOWN_PLACEHOLDER, isCountdownUrgent, useCountdown } from '@renderer/utils/countdownUtils'
import { isDigitalMode } from '@renderer/lib/runtime'
import { FaUndoAlt, FaUndo } from 'react-icons/fa'
import { FaExchangeAlt } from 'react-icons/fa'
import { useConfigStore } from '@renderer/stores/configState'
import { Button } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { unifiedCameraUtils, CameraUnavailableError } from '@renderer/utils/cameras/uniCamUtils'
import { resolveConnectArgs } from '@renderer/utils/cameras/cameraSelection'
import Preview from '@renderer/components/Preview'
import PrintPreview from '@renderer/components/PrintPreview'
import FrameOrderedLiveLayers from '@renderer/components/FrameOrderedLiveLayers'
import { flashEffect } from '@renderer/utils/flashUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import cogoToast from '@successtar/cogo-toast'
import {
  restoreOriginalSettings,
  applyLiveViewBrightness
} from '@renderer/utils/cameras/cameraSettingsManager'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { getNormalizedCaptureRatio as getSharedNormalizedCaptureRatio } from '@renderer/utils/captureRatioUtils'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { PREVIEW_CAPTURE_SHARE_FRAME_STRIP } from '@renderer/utils/previewCaptureShare'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'

const RetakePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    setSessionState,
    gallery,
    userPreferences,
    id,
    logActivity,
    userId
  } = useSessionStateStore()
  const { config: globalConfig, getPageSettings, getFrame, filters } = useConfigStore()
  const {
    isPortrait,
    isLandscape,
    isCompact: retakeCompact,
    isTiny: retakeTiny,
    designW: layoutCanvasW,
    designH: layoutCanvasH
  } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const retakeMobileLandscape = viewportCompact && isLandscapeViewport
  const retakeFabClass = retakeTiny ? 'h-7 w-7' : retakeCompact ? 'h-8 w-8' : 'h-12 w-12'
  const retakeSelectedBadgeClass = retakeTiny
    ? 'h-7 w-7'
    : retakeCompact
      ? 'h-8 w-8'
      : 'h-10 w-10'
  const config = globalConfig?.pages.retake
  const captureConfig = globalConfig?.pages.capture
  const captureMode = captureConfig?.settings.mode || 'standard'
  const filterSettings = getPageSettings('filter')
  const applyAfterCapture = filterSettings?.applyAfterCapture ?? false
  const retakeStandardPhotos = React.useMemo(
    () => gallery.capturedItems.filter((item) => item.photoUrl),
    [gallery.capturedItems]
  )
  const retakeStandardCount = retakeStandardPhotos.length
  const retakeGridLayout = React.useMemo((): { cols: string; wrap: string; cell: string } => {
    const c = retakeStandardCount
    const tinySingle = 'min-h-[min(240px,38vh)]'
    const compactSingle = 'min-h-[min(420px,40%)]'
    const singleCellMin = retakeTiny ? tinySingle : compactSingle
    if (c <= 0) return { cols: 'grid-cols-1', wrap: 'w-full max-w-full', cell: '' }
    if (retakeCompact) {
      if (retakeMobileLandscape) {
        if (c === 1) {
          return {
            cols: 'grid-cols-1',
            wrap: 'w-full max-w-md mx-auto',
            cell: singleCellMin
          }
        }
        if (c === 2) {
          return { cols: 'grid-cols-2', wrap: 'w-full max-w-full', cell: '' }
        }
        if (c === 3) {
          return { cols: 'grid-cols-3', wrap: 'w-full max-w-full', cell: '' }
        }
        return { cols: 'grid-cols-4', wrap: 'w-full max-w-full', cell: '' }
      }
      if (c <= 2) {
        return {
          cols: 'grid-cols-1',
          wrap: 'w-full max-w-md mx-auto',
          cell: singleCellMin
        }
      }
      return { cols: 'grid-cols-2', wrap: 'w-full max-w-full', cell: '' }
    }
    if (isPortrait) {
      if (c === 1) {
        return {
          cols: 'grid-cols-1',
          wrap: 'w-full max-w-xl mx-auto',
          cell: retakeTiny ? 'min-h-[min(280px,45vh)]' : 'min-h-[min(520px,52%)]'
        }
      }
      if (c === 2) {
        return {
          cols: retakeCompact ? 'grid-cols-1' : 'grid-cols-2',
          wrap: 'w-full max-w-4xl mx-auto',
          cell: retakeTiny ? 'min-h-[min(220px,36vh)]' : 'min-h-[min(400px,40%)]'
        }
      }
      if (c <= 6) {
        // 2 columns up to 6 photos: bigger cards fill the tall canvas instead of
        // a small 3-col cluster floating in space - inspecting shots (blur,
        // closed eyes) is this page's whole job (owner review round 2).
        return { cols: 'grid-cols-2', wrap: 'w-full max-w-4xl mx-auto', cell: '' }
      }
      return { cols: 'grid-cols-3', wrap: 'w-full max-w-5xl mx-auto', cell: '' }
    }
    if (c === 1) {
      return {
        cols: 'grid-cols-1',
        wrap: 'w-full max-w-2xl mx-auto',
        cell: retakeTiny ? 'min-h-[min(300px,48vh)]' : 'min-h-[min(560px,56%)]'
      }
    }
    if (c === 2) {
      return {
        cols: 'grid-cols-2',
        wrap: 'w-full max-w-6xl mx-auto',
        cell: retakeTiny ? 'min-h-[min(240px,40vh)]' : 'min-h-[min(480px,48%)]'
      }
    }
    if (c === 3) {
      return {
        cols: 'grid-cols-3',
        wrap: 'w-full max-w-[min(96%,1600px)] mx-auto',
        cell: retakeTiny ? 'min-h-[min(260px,42vh)]' : 'min-h-[min(520px,52%)]'
      }
    }
    if (c <= 4) {
      // One row, always. The old `shortSide >= 1024` gate dropped landscape to two
      // columns on any window under 1024px tall - every laptop, iPad landscape and
      // windowed booth - stacking 4 square cells into a 2x2 that scrolled half the
      // shots under the footer (field report 2026-09-14).
      return {
        cols: 'grid-cols-4',
        wrap: 'w-full max-w-7xl mx-auto',
        cell: ''
      }
    }
    if (c <= 6) {
      return { cols: 'grid-cols-3', wrap: 'w-full max-w-[1200px] mx-auto', cell: '' }
    }
    // 7+ photos landscape: 3 columns forced a 3rd row that overflowed under the
    // footer and clipped photos 7-8 to invisible slivers (wave-1 audit, critical).
    // 4 columns keeps 8 shots to 2 rows that always fit on screen.
    return { cols: 'grid-cols-4', wrap: 'w-full max-w-[min(96%,1700px)] mx-auto', cell: '' }
  }, [
    retakeStandardCount,
    retakeCompact,
    retakeTiny,
    retakeMobileLandscape,
    isPortrait,
    isLandscape
  ])

  // Class + pixel value together: the width cap below needs the gap in px, and a
  // gap that disagrees with the class would make the cap wrong.
  const retakeGridGap = React.useMemo(
    () =>
      retakeTiny
        ? { cls: 'gap-2', px: 8 }
        : retakeCompact
          ? { cls: 'gap-3', px: 12 }
          : isPortrait
            ? { cls: 'gap-10', px: 40 }
            : isLandscape
              ? { cls: 'gap-5', px: 20 }
              : { cls: 'gap-6', px: 24 },
    [retakeTiny, retakeCompact, isPortrait, isLandscape]
  )

  // PhotoFrame derives each cell's HEIGHT from its width via an inline aspect-ratio,
  // so a grid bounded only by max-width can be taller than the scroll area and push
  // whole rows under the footer. Measure the real area and cap the grid width so
  // every row fits - seeing all the shots at once is this page's entire job.
  const retakeAreaRef = useRef<HTMLDivElement>(null)
  const retakeHeaderRef = useRef<HTMLDivElement>(null)
  const [retakeGridSpace, setRetakeGridSpace] = useState({ areaH: 0, headerH: 0 })

  // Deps are empty on purpose: the area and header elements live for the lifetime of
  // standard mode, and the observer picks up every later size change - including the
  // header growing a "sessions left" line.
  React.useLayoutEffect(() => {
    const area = retakeAreaRef.current
    if (!area) return
    const read = (): void => {
      const areaStyle = window.getComputedStyle(area)
      const areaH =
        area.clientHeight -
        (parseFloat(areaStyle.paddingTop) || 0) -
        (parseFloat(areaStyle.paddingBottom) || 0)
      const header = retakeHeaderRef.current
      let headerH = 0
      if (header) {
        const headerStyle = window.getComputedStyle(header)
        headerH = header.offsetHeight + (parseFloat(headerStyle.marginBottom) || 0)
      }
      setRetakeGridSpace((prev) =>
        Math.abs(prev.areaH - areaH) < 1 && Math.abs(prev.headerH - headerH) < 1
          ? prev
          : { areaH, headerH }
      )
    }
    read()
    const observer = new ResizeObserver(read)
    observer.observe(area)
    if (retakeHeaderRef.current) observer.observe(retakeHeaderRef.current)
    return (): void => observer.disconnect()
  }, [])

  // Widest the grid may be before its rows stop fitting. The column count comes from
  // the layout table above so the two can never disagree.
  const retakeGridMaxWidth = React.useMemo((): number | undefined => {
    const colMatch = /grid-cols-(\d+)/.exec(retakeGridLayout.cols)
    const cols = colMatch ? Number(colMatch[1]) : 1
    if (!retakeStandardCount) return undefined
    const rows = Math.ceil(retakeStandardCount / cols)
    const available = retakeGridSpace.areaH - retakeGridSpace.headerH
    if (rows <= 0 || available <= 0) return undefined
    const photoRatio = userPreferences.framePhotoRatio || 1
    const cellH = (available - (rows - 1) * retakeGridGap.px) / rows
    if (cellH <= 0) return undefined
    return Math.floor(cellH * photoRatio * cols + (cols - 1) * retakeGridGap.px)
  }, [
    retakeGridLayout.cols,
    retakeStandardCount,
    retakeGridSpace,
    retakeGridGap.px,
    userPreferences.framePhotoRatio
  ])

  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
  const [isProcessingConfirm, setIsProcessingConfirm] = useState<boolean>(false)

  // Frame-based mode state
  const { remainingTime: sharedRemainingTime, isExpired: isSharedCountdownExpired } =
    useSharedCountdown()
  const selectedFrame = getFrame(userPreferences.frameId || '')
  const [retakingPhotoType, setRetakingPhotoType] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)
  const [mirroredPhotoTypes, setMirroredPhotoTypes] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [captureCountdown, setCaptureCountdown] = useState<number>(
    captureConfig?.settings.captureCountdown || 3
  )
  const [isCaptureError, setIsCaptureError] = useState(false)
  const [countdownKey, setCountdownKey] = useState(0)
  const [isCapturingWithShutter, setIsCapturingWithShutter] = useState(false)
  const [isRecoveringCamera, setIsRecoveringCamera] = useState(false)
  const [isPreviewPaused, setIsPreviewPaused] = useState(false)
  const captureCountdownHook = useCountdown(captureConfig?.settings.captureCountdown || 3, { audioCue: true })
  const initialRetakeCaptureCountdown = captureConfig?.settings.captureCountdown || 3
  const captureLockRef = useRef<boolean>(false)
  const isRecoveringRef = useRef(false)
  const previewReadyAfterRecoveryRef = useRef(false)
  // Consecutive camera recoveries with NO real captured frame in between. Bounds the
  // recovery loop so a dead DSLR (whose stale Camera object makes tryToConnect resolve
  // instantly) can't recover→resume→re-fail forever. Reset only on a captured frame.
  const consecutiveRecoveryRef = useRef(0)
  const handleRetakeCaptureRef = useRef<(() => Promise<void>) | null>(null)
  const retakingPhotoTypeRef = useRef<string | null>(null)
  const isMountedRef = useRef(true)
  const navigationLockRef = useRef(false)
  const { cameraSettings, downloadLocal, selectedCamera: selectedCameraId } = useSettingsStore()
  const cameraRecoveryContext = useCameraRecovery()

  // Video recording state and refs for frame-based mode
  const [canvasDimensions, setCanvasDimensions] = useState<{ width: number; height: number } | null>(null)
  const retakeCanvasRecorderRef = useRef<Recorder | null>(null)
  /** Dimensions the current retake recorder was encoded with. Used for drift checks before start(). */
  const recorderEncoderDimsRef = useRef<{ w: number; h: number } | null>(null)
  /** Same rotation-baked buffer as Capture / FrameBasedCapture (Preview onRecordingCanvasReady). */
  const recordingCanvasElRef = useRef<HTMLCanvasElement | null>(null)
  const sessionCanvasRecorderRef = useRef<Recorder | null>(null)
  const sessionStepIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [recordingEncoderDims, setRecordingEncoderDims] = useState<{
    width: number
    height: number
  } | null>(null)
  const onRecordingCanvasReady = useCallback(
    (el: HTMLCanvasElement, _ctx: CanvasRenderingContext2D, dim: { width: number; height: number }) => {
      recordingCanvasElRef.current = el
      setRecordingEncoderDims({ width: dim.width, height: dim.height })
    },
    []
  )
  const recordingActiveRef = useRef<boolean>(false)
  const recordingPromiseRef = useRef<Promise<void> | null>(null)

  // Video recording configuration
  const useRecording = userPreferences.useRecording
  const FPS = 30
  const recordingSpeed = captureConfig?.settings?.recordingSpeed || 1
  const targetDuration = (captureConfig?.settings.captureCountdown || 3) * 1000
  const totalFrames = Math.ceil((targetDuration * FPS) / 1000)

  // Helper to validate dimensions for H.264 codec (requires even numbers)
  const validateDimensions = (width: number, height: number): { width: number; height: number } => {
    // Round down to nearest even number
    const validatedWidth = Math.floor(width / 2) * 2
    const validatedHeight = Math.floor(height / 2) * 2
    return { width: validatedWidth, height: validatedHeight }
  }

  // Get unique photo types from frame for frame-based mode
  const frameBasedUniquePhotoTypes =
    captureMode === 'frame-based' && selectedFrame
      ? new Set(
          selectedFrame.drawParams.images
            .map((image) => image.type)
            .filter((type) => type.startsWith('photo-'))
        )
      : new Set<string>()
  const frameBasedUniquePhotoTypesArray = Array.from(frameBasedUniquePhotoTypes).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  )
  const frameBasedPhotoSlots =
    captureMode === 'frame-based' && selectedFrame
      ? selectedFrame.drawParams.images.filter((img) => img.type.startsWith('photo-'))
      : []

  // Build map of photos by type from captured items (frame-based mode).
  // Prefer stable mapping via item.photoType; fall back to positional mapping for legacy sessions.
  const capturedPhotosByType = new Map<string, string>()
  if (captureMode === 'frame-based') {
    const photosOnly = gallery.capturedItems.filter(
      (item) =>
        item.photoUrl &&
        !item.id?.includes('dreambooth-strip') &&
        !item.id?.includes('dreambooth-gif')
    )

    const hasPhotoTypeMetadata = photosOnly.some((item) => Boolean(item.photoType))
    if (hasPhotoTypeMetadata) {
      for (const item of photosOnly) {
        const photoType = item.photoType
        if (!photoType) continue
        if (!photoType.startsWith('photo-')) continue
        if (!frameBasedUniquePhotoTypesArray.includes(photoType)) continue
        capturedPhotosByType.set(photoType, item.photoUrl || '')
      }
    } else {
      const firstNPhotos = photosOnly.slice(0, frameBasedUniquePhotoTypesArray.length)
      firstNPhotos.forEach((item, index) => {
        const photoType = frameBasedUniquePhotoTypesArray[index]
        if (photoType) capturedPhotosByType.set(photoType, item.photoUrl || '')
      })
    }
  }

  const maxPhotos = config?.settings?.maxRetakeCount
  const retakeTimeout = config?.settings?.retakeTimeout || 60
  const maxRetakeSession = config?.settings?.maxRetakeSession

  // Initialize countdown hook
  const retakeCountdown = useCountdown(retakeTimeout)

  // Count how many retake sessions have occurred
  const retakeSessionCount = userPreferences.retakeSessionsCompleted || 0

  // Check if retakes are still allowed
  const canRetake =
    maxRetakeSession === undefined ||
    maxRetakeSession === null ||
    retakeSessionCount < maxRetakeSession

  // Remaining retake sessions (only meaningful when maxRetakeSession is configured)
  const remainingRetakeSessions =
    typeof maxRetakeSession === 'number'
      ? Math.max(0, maxRetakeSession - retakeSessionCount)
      : undefined

  const stopSessionRecordingAndUpload = useCallback(async (): Promise<void> => {
    if (sessionStepIntervalRef.current) {
      clearInterval(sessionStepIntervalRef.current)
      sessionStepIntervalRef.current = null
    }
    if (!useRecording || userPreferences.recordingType !== 'session_recording') return
    if (!sessionCanvasRecorderRef.current) return
    try {
      const recordedChunks = await sessionCanvasRecorderRef.current.stop()
      const mp4Blob = new Blob([recordedChunks as ArrayBuffer], { type: 'video/mp4' })
      const mp4Url = URL.createObjectURL(mp4Blob)
      const videoFile = new File([mp4Blob], `dreambooth-${gallery.id}-retake-session.mp4`, {
        type: 'video/mp4'
      })
      backgroundVideoProcessor.processVideo({
        id: `session-retake-${gallery.id}-${Date.now()}`,
        type: 'session',
        resources: [],
        frameConfig: {
          id: 'capture-video',
          baseImage: '',
          drawParams: {
            canvasWidth: canvasDimensions?.width || 1200,
            canvasHeight: canvasDimensions?.height || 1800,
            backgroundColor: '#FFFFFF',
            images: []
          }
        },
        // The session id, not gallery.id (the galleryKey), so the retake recording
        // lands in the gallery doc the guest link opens.
        sessionId: id || gallery.id,
        userId: userId || '',
        projectId: projectId || '',
        galleryId: gallery.id,
        videoFile,
        onComplete: () => {
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          logActivity('Session recording uploaded (retake).', { type: 'recording' })
          setSessionState({
            gallery: {
              capturedItems: [
                ...prevItems,
                {
                  id: 'dreambooth-session-recording-' + new Date().getTime(),
                  recordingUrl: mp4Url
                }
              ]
            }
          })
        },
        onError: (error) => {
          console.error('Error processing session video (retake):', error)
          cogoToast.error(
            t('capture.errorProcessingSessionVideo', 'Error processing session video')
          )
        }
      })
    } catch (e) {
      console.error('[Retake] stopSessionRecording:', e)
    }
    sessionCanvasRecorderRef.current = null
  }, [
    useRecording,
    userPreferences.recordingType,
    canvasDimensions,
    gallery.id,
    userId,
    projectId,
    setSessionState,
    logActivity,
    t
  ])

  // Frame-based mode: Navigate away if countdown expired
  useEffect(() => {
    if (
      captureMode === 'frame-based' &&
      isSharedCountdownExpired &&
      !navigationLockRef.current &&
      isMountedRef.current
    ) {
      navigationLockRef.current = true
      logActivity('Shared countdown expired on Retake page (frame-based mode)')
      void (async (): Promise<void> => {
        await stopSessionRecordingAndUpload()
        if (!isMountedRef.current) return
        const applyAfterCaptureOnTimeout = filterSettings?.applyAfterCapture || false
        const filterEnabledOnTimeout = filterSettings?.enabled
        if (applyAfterCaptureOnTimeout && filterEnabledOnTimeout) {
          navigate(`/${projectId}/filter`)
        } else {
          navigate(`/${projectId}/select`)
        }
        setTimeout(() => {
          navigationLockRef.current = false
        }, 1000)
      })()
    }
  }, [captureMode, isSharedCountdownExpired, stopSessionRecordingAndUpload, navigate, projectId, filterSettings])

  // Frame-based: preview dimensions updated after camera recovery
  useEffect(() => {
    if (captureMode !== 'frame-based') return
    if (isRecoveringRef.current && canvasDimensions) {
      console.log('[Retake RECOVERY] Preview is ready after recovery')
      previewReadyAfterRecoveryRef.current = true
    }
  }, [captureMode, canvasDimensions])

  // Frame-based mode: initial callback setup (will be refreshed before each start)
  useEffect(() => {
    if (captureMode !== 'frame-based') return

    captureCountdownHook.setOnTick(() => {
      if (isRecoveringRef.current) return
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      if (isRecoveringRef.current) {
        console.log('[Retake COUNTDOWN] Recovery in progress, skipping capture')
        return
      }
      void handleRetakeCaptureRef.current?.()
    })

    return (): void => {
      // Don't stop on cleanup - let handleRetakePhoto manage starting/stopping
    }
  }, [captureMode])

  /** Reset countdown, set fresh callbacks, and start — mirrors FrameBasedCapture helper. */
  const startRetakeCaptureCountdown = (): void => {
    const captureTime = captureConfig?.settings.captureCountdown || 3
    captureCountdownHook.stop()
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)
    setCountdownKey((prev) => prev + 1)

    captureCountdownHook.setOnTick(() => {
      if (isRecoveringRef.current) return
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })
    captureCountdownHook.setOnComplete(() => {
      if (isRecoveringRef.current) {
        console.log('[Retake COUNTDOWN] Recovery in progress, skipping capture')
        return
      }
      void handleRetakeCaptureRef.current?.()
    })

    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
    setCapturing(true)
  }

  // Keep ref in sync with state
  useEffect(() => {
    if (captureMode === 'frame-based') {
      retakingPhotoTypeRef.current = retakingPhotoType
    }
  }, [captureMode, retakingPhotoType])

  // Sync countdown state when capturing becomes true
  useEffect(() => {
    if (captureMode === 'frame-based' && capturing) {
      const currentRemaining = captureCountdownHook.getRemainingSeconds()
      if (currentRemaining > 0 && currentRemaining !== captureCountdown) {
        setCaptureCountdown(currentRemaining)
      }
    }
  }, [captureMode, capturing])

  // session_recording: full-session MP4 on Retake (frame mode), same encoder canvas as strip_live/strip_story.
  // strip_live / strip_story: encoder binds to Preview's rotation-baked 2D buffer.
  useEffect(() => {
    if (captureMode !== 'frame-based') return
    if (
      !useRecording ||
      !retakingPhotoType ||
      !recordingEncoderDims ||
      recordingEncoderDims.width <= 0
    )
      return
    if (
      userPreferences.recordingType !== 'strip_live' &&
      userPreferences.recordingType !== 'strip_story'
    )
      return

    const rec = recordingCanvasElRef.current
    const recCtx = rec?.getContext('2d')
    if (!rec || !recCtx) return

    if (retakeCanvasRecorderRef.current) {
      try {
        retakeCanvasRecorderRef.current.stop()
      } catch {
        /* ignore */
      }
      retakeCanvasRecorderRef.current = null
    }

    const validatedDims = validateDimensions(
      rec.width > 0 ? rec.width : recordingEncoderDims.width,
      rec.height > 0 ? rec.height : recordingEncoderDims.height
    )
    recorderEncoderDimsRef.current = { w: validatedDims.width, h: validatedDims.height }

    try {
      retakeCanvasRecorderRef.current = new Recorder(recCtx as unknown as RenderingContext, {
        name: `dreambooth-${id}-retake-${retakingPhotoType}`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: validatedDims.width,
          height: validatedDims.height,
          bitrate: 2000000
        },
        duration: Infinity,
        download:
          downloadLocal &&
          (userPreferences.recordingType === 'strip_live' ||
            userPreferences.recordingType === 'strip_story'),
        frameRate: FPS
      })
      console.log(
        `[Retake] Recorder created ${validatedDims.width}x${validatedDims.height} for ${retakingPhotoType}`
      )
    } catch (error) {
      console.error('[Retake] Error creating recorder:', error)
      retakeCanvasRecorderRef.current = null
    }

    return (): void => {
      /* avoid stopping mid-retake when deps churn; full cleanup on unmount below */
    }
  }, [
    captureMode,
    retakingPhotoType,
    recordingEncoderDims,
    useRecording,
    userPreferences.recordingType,
    downloadLocal,
    id,
    FPS
  ])

  useEffect(() => {
    if (captureMode !== 'frame-based') return
    if (!useRecording || userPreferences.recordingType !== 'session_recording') return
    const rec = recordingCanvasElRef.current
    const recCtx = rec?.getContext('2d')
    if (!rec || !recCtx || !recordingEncoderDims || recordingEncoderDims.width <= 0) return

    if (sessionCanvasRecorderRef.current) {
      try {
        sessionCanvasRecorderRef.current.stop()
      } catch {
        /* ignore */
      }
      sessionCanvasRecorderRef.current = null
    }
    if (sessionStepIntervalRef.current) {
      clearInterval(sessionStepIntervalRef.current)
      sessionStepIntervalRef.current = null
    }

    const validatedDims = validateDimensions(
      rec.width > 0 ? rec.width : recordingEncoderDims.width,
      rec.height > 0 ? rec.height : recordingEncoderDims.height
    )
    recorderEncoderDimsRef.current = { w: validatedDims.width, h: validatedDims.height }

    try {
      sessionCanvasRecorderRef.current = new Recorder(recCtx as unknown as RenderingContext, {
        name: `dreambooth-${id}-retake-session`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          width: validatedDims.width,
          height: validatedDims.height,
          bitrate: 2000000,
          latencyMode: 'realtime'
        },
        duration: Infinity,
        download: downloadLocal,
        frameRate: FPS
      })
      console.log(
        `[Retake] Session recorder created ${validatedDims.width}x${validatedDims.height}`
      )
    } catch (error) {
      console.error('[Retake] Error creating session recorder:', error)
      sessionCanvasRecorderRef.current = null
    }

    void (async (): Promise<void> => {
      const sessionRec = sessionCanvasRecorderRef.current
      if (!sessionRec) return
      try {
        await sessionRec.start()
        const stepIntervalMs = 1000 / (FPS / recordingSpeed)
        sessionStepIntervalRef.current = setInterval(async () => {
          if (sessionCanvasRecorderRef.current?.status !== RecorderStatus.Recording) return
          try {
            await sessionCanvasRecorderRef.current?.step()
          } catch (err) {
            console.error('[Retake] session recorder step', err)
          }
        }, stepIntervalMs)
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e)
        console.error('[Retake] Error starting session recorder', e)
        logActivity(`Failed to start session recorder (retake): ${errorMessage}`, {
          error: errorMessage,
          component: 'session_recorder',
          level: 'error'
        })
      }
    })()

    return (): void => {
      /* Same as FrameBasedCapture: do not stop on dep churn; unmount effect cleans up. */
    }
  }, [
    captureMode,
    useRecording,
    userPreferences.recordingType,
    recordingEncoderDims,
    downloadLocal,
    id,
    FPS,
    recordingSpeed
  ])

  useEffect(() => {
    return (): void => {
      if (sessionStepIntervalRef.current) {
        clearInterval(sessionStepIntervalRef.current)
        sessionStepIntervalRef.current = null
      }
      try {
        sessionCanvasRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
      sessionCanvasRecorderRef.current = null
      try {
        retakeCanvasRecorderRef.current?.stop()
      } catch {
        /* ignore */
      }
      retakeCanvasRecorderRef.current = null
    }
  }, [])

  // Imperative frame recording loop (frame-based mode). Called from handleRetakePhoto after recorder.start().
  const runFrameRecordingLoop = async (photoType: string): Promise<void> => {
    if (captureMode !== 'frame-based' || !useRecording || !retakeCanvasRecorderRef.current) return
    if (retakeCanvasRecorderRef.current.status !== RecorderStatus.Recording) return
    if (recordingActiveRef.current) return
    recordingActiveRef.current = true
    let frameCount = 0
    const startTime = performance.now()
    while (
      recordingActiveRef.current &&
      frameCount < totalFrames &&
      retakeCanvasRecorderRef.current?.status === RecorderStatus.Recording
    ) {
      const elapsedTime = performance.now() - startTime
      const targetFrameTime = (frameCount * 1000) / FPS
      if (elapsedTime >= targetFrameTime) {
        try {
          await retakeCanvasRecorderRef.current.step()
          frameCount++
        } catch (error) {
          console.error('[Retake] Error stepping recorder', error)
        }
      }
      await new Promise((r) => setTimeout(r, 1))
    }
    recordingActiveRef.current = false
    if (frameCount > 0) {
      console.log(`[Retake] Finished recording frames for photo type ${photoType}: ${frameCount}/${totalFrames} frames recorded`)
    }
  }


  useEffect((): void | (() => void) => {
    isMountedRef.current = true
    
    // Frame-based mode: skip standard retake logic
    if (captureMode === 'frame-based') {
      stateUploader.uploadSessionState().catch(() => {})
      return () => {
        isMountedRef.current = false
      }
    }

    if (!canRetake) {
      if (isMountedRef.current) {
        if (applyAfterCapture && filterSettings?.enabled) {
          navigate(`/${projectId}/filter`)
        } else {
          navigate(`/${projectId}/select`)
        }
      }
      return () => {
        isMountedRef.current = false
      }
    }

    // Upload session state on mount (fire-and-forget)
    stateUploader.uploadSessionState().catch(() => {})

    if (retakeTimeout > 0) {
      // Set up countdown callbacks
      retakeCountdown.setOnTick(() => {
        if (isMountedRef.current) {
          setRemainingTime(retakeCountdown.getFormattedRemainingSeconds())
        }
      })

      retakeCountdown.setOnComplete(() => {
        if (!isMountedRef.current) return
        // Log countdown timeout
        logActivity('Retake selection timed out.', {
          selectedCount: selectedPhotos.length,
          timeoutDuration: retakeTimeout
        })
        handleContinue()
      })

      retakeCountdown.start()

      return (): void => {
        isMountedRef.current = false
        retakeCountdown.stop()
        retakeCountdown.setOnComplete(null)
        retakeCountdown.setOnTick(null)
      }
    }
    
    return () => {
      isMountedRef.current = false
    }
  }, [captureMode])

  // Frame-based mode helpers — delegate to shared helper for consistent Number.isFinite guard.
  const getNormalizedCaptureRatio = (): number | undefined =>
    getSharedNormalizedCaptureRatio(cameraSettings.captureRatio)

  const getPhotoRatio = (slot: (typeof frameBasedPhotoSlots)[0]): number => {
    return slot.width / slot.height
  }

  // Get selected filter for frame-based mode
  const selectedFilterId = userPreferences.filterId
  const selectedFilter = selectedFilterId
    ? filters.find((f) => f._id === selectedFilterId) || filters[0]
    : filters[0]
  const filterAdjustments = selectedFilter?.adjustments

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (applyAfterCapture) return
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (filterAdjustments) {
      filterUtils.applyAdjustments(ctx, filterAdjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  // Frame-based mode: Handle retake photo
  const handleRetakePhoto = async (photoType: string): Promise<void> => {
    if (capturing || isSharedCountdownExpired) return

    logActivity(`Starting retake for photo type ${photoType} (frame-based mode)`)
    setRetakingPhotoType(photoType)
    setIsCaptureError(false)

    // Wait for React to re-render and Preview component to mount before looking for canvas
    await new Promise((resolve) => setTimeout(resolve, 200))

    if (
      useRecording &&
      (userPreferences.recordingType === 'strip_live' ||
        userPreferences.recordingType === 'strip_story')
    ) {
      try {
        let tries = 0
        while (
          tries < 80 &&
          (!retakeCanvasRecorderRef.current ||
            retakeCanvasRecorderRef.current.status !== RecorderStatus.Ready)
        ) {
          await new Promise((r) => setTimeout(r, 50))
          tries++
        }
        const r = retakeCanvasRecorderRef.current
        if (r?.status === RecorderStatus.Ready) {
          const recEl =
            recordingCanvasElRef.current ||
            (document.querySelector(
              'canvas[data-preview-type="camera-recording"]'
            ) as HTMLCanvasElement | null)
          const enc = recorderEncoderDimsRef.current
          if (recEl && enc && (recEl.width !== enc.w || recEl.height !== enc.h)) {
            console.warn(
              `[Retake] encoder (${enc.w}x${enc.h}) diverged from recording canvas (${recEl.width}x${recEl.height}) before start`
            )
          }
          await r.start()
          console.log(`[Retake] Recorder started for photo type ${photoType}, status: ${r.status}`)
        } else {
          console.warn(`[Retake] Recorder not Ready for photo type ${photoType}, skipping start`)
          logActivity('Retake recorder not Ready — this retake will have no video clip', {
            photoType,
            component: 'capture_recorder',
            level: 'error'
          })
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        console.error(`[Retake] Error starting recorder for photo type ${photoType}:`, error)
        logActivity(`Failed to start retake recorder: ${errorMessage}`, {
          error: errorMessage,
          photoType,
          component: 'capture_recorder',
          level: 'error'
        })
      }
    }

    startRetakeCaptureCountdown()
    // Imperative frame recording: start immediately (no useEffect race)
    if (useRecording && retakeCanvasRecorderRef.current?.status === RecorderStatus.Recording) {
      recordingPromiseRef.current = runFrameRecordingLoop(photoType)
    }
  }

  // A page unmount mid-recovery (session reset, navigation) must not leave the global
  // recovery hold set — that would silence the View health monitor and idle-eject for
  // the rest of the app's life.
  useEffect(() => {
    return () => {
    unifiedCameraUtils.setRecovering('primary', false)
    }
  }, [])

  const onCameraReconnected = (): void => {
    console.log('[Retake RECOVERY] Camera reconnected, resuming retake capture')
    isRecoveringRef.current = false
    unifiedCameraUtils.setRecovering('primary', false)
    previewReadyAfterRecoveryRef.current = false
    setIsPreviewPaused(false)
    setIsRecoveringCamera(false)
    setIsCaptureError(false)
    setIsCapturingWithShutter(false)

    cogoToast.success(
      t('capture.cameraReconnected', 'Camera reconnected successfully. Resuming capture...')
    )

    logActivity('Camera recovery completed, resuming frame-based retake', {
      photoType: retakingPhotoTypeRef.current,
      isRetake: true
    })

    const captureTime = captureConfig?.settings.captureCountdown || 3
    captureCountdownHook.setRemainingSeconds(captureTime)
    setCaptureCountdown(captureTime)

    const photoType = retakingPhotoTypeRef.current
    if (photoType) {
      window.setTimeout(() => {
        void handleRetakePhoto(photoType)
      }, 800)
    }
  }

  const handleCameraRecovery = async (): Promise<void> => {
    if (isRecoveringCamera) {
      console.log('[RECOVERY] Recovery already in progress')
      return
    }

    // Loop-breaker: count this recovery. It resets to 0 only when a real frame is
    // captured. Once we exceed MAX_CONSECUTIVE_RECOVERIES with no captured frame in
    // between, stop trusting the autonomous fast reconnect and go straight to the
    // operator CameraPrompt (which holds-for-operator on paid sessions).
    const MAX_CONSECUTIVE_RECOVERIES = 3
    consecutiveRecoveryRef.current++
    const forceOperatorPrompt = consecutiveRecoveryRef.current > MAX_CONSECUTIVE_RECOVERIES

    console.log('[RECOVERY] Starting camera recovery process (frame-based retake)')
    previewReadyAfterRecoveryRef.current = false
    isRecoveringRef.current = true
    unifiedCameraUtils.setRecovering('primary', true)
    setIsRecoveringCamera(true)
    setIsPreviewPaused(true)
    setIsCaptureError(false)
    setCapturing(false)
    setIsCapturingWithShutter(false)
    captureCountdownHook.stop()
    captureCountdownHook.setOnComplete(null)
    setCaptureCountdown(initialRetakeCaptureCountdown)

    logActivity('Camera recovery process started (frame-based retake)', {
      photoType: retakingPhotoTypeRef.current,
      isRetake: true
    })

    const preservedSelectedCamera = selectedCameraId
    console.log('[RECOVERY] Preserved selectedCamera:', preservedSelectedCamera)

    if (!preservedSelectedCamera) {
      console.error('[RECOVERY] No selectedCamera found, cannot recover')
      cogoToast.error(
        t(
          'capture.recoveryNoCamera',
          'No camera selected. Please select a camera in settings and try again.'
        ),
        { hideAfter: 5 }
      )
      setIsRecoveringCamera(false)
      setIsPreviewPaused(false)
      isRecoveringRef.current = false
      unifiedCameraUtils.setRecovering('primary', false)
      return
    }

    try {
      if (!forceOperatorPrompt) {
        console.log('[RECOVERY] Attempting simple reconnect first (no disconnect)')
        cogoToast.info(t('capture.recoveryReconnecting', 'Reconnecting camera...'))

        try {
          const { setSelectedCamera } = useSettingsStore.getState()
          if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
            console.log(
              '[RECOVERY] Restoring selectedCamera before reconnect:',
              preservedSelectedCamera
            )
            setSelectedCamera(preservedSelectedCamera)
          }

          // resolveConnectArgs handles webcam deviceIds, legacy bare productIds,
          // 'detect', and the serial-qualified usb:vid:pid:serial format.
          const { deviceType, params } = resolveConnectArgs(preservedSelectedCamera)

          const reconnectPromise = unifiedCameraUtils.tryToConnect(
            deviceType,
            params,
            userPreferences.selectedCamera as 'primary' | 'secondary'
          )
          const timeoutPromise = new Promise((_resolve, reject) => {
            setTimeout(() => {
              reject(new Error('Reconnection timed out'))
            }, 5000)
          })

          await Promise.race([reconnectPromise, timeoutPromise])

          // tryToConnect RESOLVES even when a stale Camera object is still in the store
          // — the camera branch short-circuits on `if (!currentCamera)` and returns
          // without touching the hardware. A bare resolve is NOT proof the DSLR is alive;
          // that false success is what looped paid sessions forever. Verify with a
          // side-effect-free test frame before resuming (capturePreviewForQR never nulls
          // the camera or clears selectedCamera on failure; capped so a hung read can't
          // stall recovery). No frame → fall through to the operator CameraPrompt.
          if (deviceType === 'camera') {
            const camKey = userPreferences.selectedCamera as 'primary' | 'secondary'
            let testFrame: Blob | undefined
            for (let probe = 0; probe < 2 && !testFrame; probe++) {
              if (probe > 0) await new Promise((r) => setTimeout(r, 300))
              testFrame = await Promise.race([
                unifiedCameraUtils.capturePreviewForQR(camKey),
                new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 1500))
              ])
            }
            if (!testFrame) {
              throw new Error('Reconnect verification failed: camera returned no frame')
            }
          }

          console.log('[RECOVERY] Simple reconnect successful!')
          cogoToast.success(t('capture.recoveryReconnected', 'Camera reconnected!'), { hideAfter: 2 })

          onCameraReconnected()
          return
        } catch (reconnectError) {
          console.log(
            '[RECOVERY] Simple reconnect failed, will disconnect and reconnect:',
            reconnectError
          )
        }
      } else {
        console.warn(
          `[RECOVERY] ${consecutiveRecoveryRef.current} consecutive auto-recoveries with no captured frame — skipping fast reconnect, escalating to operator`
        )
        logActivity(
          'Camera recovery escalating to operator (repeated auto-recovery made no progress)',
          {
            consecutiveRecoveries: consecutiveRecoveryRef.current,
            photoType: retakingPhotoTypeRef.current,
            level: 'error'
          }
        )
      }

      console.log('[RECOVERY] Disconnecting camera before reconnection')
      cogoToast.info(t('capture.recoveryDisconnecting', 'Disconnecting camera...'), {
        hideAfter: 2
      })

      try {
        await new Promise((resolve) => setTimeout(resolve, 500))
        await unifiedCameraUtils.disconnect(
          userPreferences.selectedCamera as 'primary' | 'secondary',
          'capture-recovery'
        )
        console.log('[RECOVERY] Camera disconnected')
      } catch (disconnectError) {
        console.warn('[RECOVERY] Camera disconnect had issues, continuing:', disconnectError)
      }

      const { setSelectedCamera } = useSettingsStore.getState()
      if (!useSettingsStore.getState().selectedCamera && preservedSelectedCamera) {
        console.log(
          '[RECOVERY] Restoring selectedCamera after disconnect:',
          preservedSelectedCamera
        )
        setSelectedCamera(preservedSelectedCamera)
      }

      await new Promise((resolve) => setTimeout(resolve, 1000))

      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        console.log('[RECOVERY] Triggering CameraPrompt for user reconnection')
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        console.error('[RECOVERY] onCameraRecoveryRequest not available in context')
        cogoToast.error(
          t(
            'capture.recoveryContextError',
            'Recovery system error. Please reconnect camera manually.'
          )
        )
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
        isRecoveringRef.current = false
        unifiedCameraUtils.setRecovering('primary', false)
      }
    } catch (error) {
      console.error('[RECOVERY] Error during camera recovery:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      cogoToast.error(
        t('capture.recoveryError', 'Camera recovery failed. Please reconnect manually.'),
        { hideAfter: 5 }
      )
      logActivity(`Camera recovery error (retake): ${errorMessage}`, {
        error: errorMessage,
        photoType: retakingPhotoTypeRef.current,
        level: 'error'
      })
      if (cameraRecoveryContext?.onCameraRecoveryRequest) {
        cameraRecoveryContext.onCameraRecoveryRequest(onCameraReconnected)
      } else {
        setIsRecoveringCamera(false)
        setIsPreviewPaused(false)
        isRecoveringRef.current = false
        unifiedCameraUtils.setRecovering('primary', false)
      }
    }
  }

  // Frame-based mode: Handle retake capture
  const handleRetakeCapture = async (): Promise<void> => {
    const currentPhotoType = retakingPhotoTypeRef.current
    if (captureLockRef.current || currentPhotoType === null) {
      return
    }

    captureLockRef.current = true

    const cameraKey = userPreferences.selectedCamera as 'primary' | 'secondary'

    try {
      console.log('Starting retake capture for photo type', currentPhotoType)

      flashEffect('.flash')
      setCapturing(false)
      setIsCaptureError(false)
      const captureTime = captureConfig?.settings.captureCountdown || 3
      setCaptureCountdown(captureTime)

      // Stop frame recording and wait for loop to finish before stopping recorder
      recordingActiveRef.current = false
      if (recordingPromiseRef.current) {
        await recordingPromiseRef.current
      }
      recordingPromiseRef.current = null

      if (cameraSettings.useShutterCapture && useCameraStore.getState().deviceType === 'camera') {
        setIsCapturingWithShutter(true)
      }

      if (cameraSettings.autoBrightenLiveViewForFlash && cameraSettings.useShutterCapture) {
        try {
          await restoreOriginalSettings(cameraKey)
          console.log('[Retake] Restored original camera settings before retake capture')
        } catch (err) {
          console.error('[Retake] Failed to restore original settings:', err)
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 200))

      let imageData: File | undefined
      try {
        imageData = await unifiedCameraUtils.captureImage(
          cameraKey,
          applyAfterCapture ? (): void => {} : applyFilter,
          getNormalizedCaptureRatio(),
          !cameraSettings.useShutterCapture
        )
      } finally {
        setIsCapturingWithShutter(false)
      }

      // Stop and save recording
      let retakeRecordingUrl: string | undefined = undefined
      if (useRecording && retakeCanvasRecorderRef.current) {
        const recorderStatus = retakeCanvasRecorderRef.current.status
        console.log(`[Retake] Stopping recorder for photo type ${currentPhotoType}, current status: ${recorderStatus}`)
        
        if (recorderStatus === RecorderStatus.Recording) {
          try {
            const captureChunks = await retakeCanvasRecorderRef.current.stop()
            if (!captureChunks) {
              console.warn(`[Retake] Recorder returned no chunks for photo type ${currentPhotoType}`)
            } else {
              const chunksArray = Array.isArray(captureChunks) 
                ? captureChunks 
                : [captureChunks as ArrayBuffer]
              const chunksLength = Array.isArray(captureChunks) 
                ? captureChunks.length 
                : (captureChunks as ArrayBuffer).byteLength
              console.log(`[Retake] Recorder stopped for photo type ${currentPhotoType}, chunks length: ${chunksLength}`)
              const retakeBlob = new Blob(chunksArray as BlobPart[], { type: 'video/mp4' })
              console.log(`[Retake] Created blob for photo type ${currentPhotoType}, size: ${retakeBlob.size} bytes`)
              
              // Validate blob size (should be > 0)
              if (retakeBlob.size > 0) {
                retakeRecordingUrl = URL.createObjectURL(retakeBlob)
                console.log(`[Retake] Created video blob URL for photo type ${currentPhotoType}: ${retakeRecordingUrl}, size: ${retakeBlob.size} bytes`)
              } else {
                console.warn(`[Retake] Video blob is empty for photo type ${currentPhotoType}, skipping video URL creation`)
              }
            }
          } catch (error) {
            console.error(`[Retake] Error stopping recorder for photo type ${currentPhotoType}:`, error)
          }
        } else {
          console.warn(`[Retake] Recording enabled but recorder not available when stopping photo type ${currentPhotoType} (status: ${recorderStatus})`)
        }
      }

      if (imageData) {
        console.log('Successfully retook image')
        // Real frame captured — recovery (if any) made genuine progress; reset the
        // consecutive-recovery loop-breaker (reset on a captured frame, never on reconnect).
        consecutiveRecoveryRef.current = 0
        const imageUrl = URL.createObjectURL(imageData)

        if (cameraSettings.autoBrightenLiveViewForFlash && cameraSettings.useShutterCapture) {
          try {
            await applyLiveViewBrightness(cameraKey, {
              autoBrightenLiveViewForFlash: cameraSettings.autoBrightenLiveViewForFlash,
              liveViewShutterSpeedOverride: cameraSettings.liveViewShutterSpeedOverride
            })
            console.log('[Retake] Re-applied live view brightness settings after retake capture')
          } catch (err) {
            console.error('[Retake] Failed to re-apply live view brightness:', err)
          }
        }

        // Get the LATEST capturedItems from store
        const latestCapturedItems = useSessionStateStore.getState().gallery.capturedItems || []
        const photosOnly = latestCapturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif')
        )
        const updatedItems = [...latestCapturedItems]

        // Match display logic in capturedPhotosByType: prefer photoType metadata over positional mapping
        const hasPhotoTypeMetadata = photosOnly.some((item) => Boolean(item.photoType))
        const photoTypeIndex = frameBasedUniquePhotoTypesArray.indexOf(currentPhotoType)
        let galleryItemIndex = -1
        if (hasPhotoTypeMetadata) {
          galleryItemIndex = latestCapturedItems.findIndex((item) => item.photoType === currentPhotoType)
        } else {
          const firstNPhotos = photosOnly.slice(0, frameBasedUniquePhotoTypesArray.length)
          if (photoTypeIndex >= 0 && photoTypeIndex < firstNPhotos.length) {
            const targetItem = firstNPhotos[photoTypeIndex]
            galleryItemIndex = latestCapturedItems.findIndex((item) => item.id === targetItem.id)
          }
        }

        // Preserve original recordingUrl if retake recording failed
        const originalItem = galleryItemIndex >= 0 ? latestCapturedItems[galleryItemIndex] : null
        const preservedRecordingUrl = originalItem?.recordingUrl
        // When applyAfterCapture: retaken photo is raw; clear stale filteredVersions and use new image as original
        const preservedOriginalPhotoUrl = applyAfterCapture ? imageUrl : (originalItem?.originalPhotoUrl || imageUrl)

        // Update the gallery item at the found index
        if (galleryItemIndex >= 0 && galleryItemIndex < updatedItems.length) {
          updatedItems[galleryItemIndex] = {
            ...updatedItems[galleryItemIndex],
            photoUrl: imageUrl,
            photoType: currentPhotoType,
            originalPhotoUrl: preservedOriginalPhotoUrl,
            recordingUrl: retakeRecordingUrl || preservedRecordingUrl,
            filteredVersions: applyAfterCapture ? {} : (originalItem?.filteredVersions ?? {}),
            timestamp: new Date()
          }
        } else {
          updatedItems.push({
            id: `dreambooth-${id || 'unknown'}-retake-${Date.now()}`,
            photoUrl: imageUrl,
            photoType: currentPhotoType,
            originalPhotoUrl: imageUrl,
            recordingUrl: retakeRecordingUrl,
            timestamp: new Date()
          })
        }

        setSessionState({
          gallery: {
            capturedItems: updatedItems
          }
        })

        logActivity(`Retook photo type ${currentPhotoType} (frame-based mode)`, {
          photoType: currentPhotoType,
          photoTypeIndex,
          galleryItemIndex,
          hasNewRecording: !!retakeRecordingUrl
        })

        setRetakingPhotoType(null)
        setCapturing(false)
      } else {
        // De-gated from `useShutterCapture`: that requirement made this unreachable for
        // the liveview path (which requires useShutterCapture === false, the default).
        const deviceType = useCameraStore.getState().deviceType
        const isCameraHardwareFailure = deviceType === 'camera' && !imageData

        // Classified at the capture boundary — the REAL error (focus/busy/USB).
        const lastCaptureError = unifiedCameraUtils.getLastCaptureError('primary')

        if (isCameraHardwareFailure) {
          // Focus miss ≠ hardware failure: retry the shot, don't reconnect.
          if (lastCaptureError?.kind === 'focus') {
            console.log('[Retake] Shutter failed on focus; retrying without recovery')
            cogoToast.warn(
              t(
                'capture.focusFailed',
                'Camera could not focus. Please step back slightly — retrying...'
              ),
              { hideAfter: 4 }
            )
            logActivity('Retake capture failed: focus not acquired, retrying in place', {
              error: lastCaptureError.message,
              errorKind: lastCaptureError.kind,
              photoType: currentPhotoType,
              level: 'error'
            })
            setIsCaptureError(true)
            return
          }

          console.log('[Retake] Camera hardware failure detected, triggering recovery')
          cogoToast.error(
            t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
            { hideAfter: 4 }
          )
          logActivity('Camera hardware failure during retake, starting recovery.', {
            error: lastCaptureError?.message ?? 'No image data captured',
            errorKind: lastCaptureError?.kind ?? 'unknown',
            photoType: currentPhotoType,
            level: 'error',
            recovery: true
          })
          await new Promise((resolve) => setTimeout(resolve, 500))
          void handleCameraRecovery()
          return
        }

        logActivity('Retake capture failed: No image data captured.', {
          error: lastCaptureError?.message ?? 'No image data captured',
          errorKind: lastCaptureError?.kind ?? 'unknown',
          level: 'error',
          photoType: currentPhotoType
        })
        setIsCaptureError(true)
      }
    } catch (error) {
      console.error('Retake capture failed:', error)
      setIsCapturingWithShutter(false)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      const deviceType = useCameraStore.getState().deviceType
      const isCameraHardwareFailure =
        error instanceof CameraUnavailableError ||
        (deviceType === 'camera' &&
          (errorMessage.includes('LIBUSB') ||
            errorMessage.includes('camera') ||
            errorMessage.includes('device') ||
            errorMessage.includes('USB')))

      if (isCameraHardwareFailure) {
        console.log('[Retake] Camera hardware failure in catch, triggering recovery')
        cogoToast.error(
          t('capture.captureFailed', 'Capture failed. Attempting to recover camera...'),
          { hideAfter: 4 }
        )
        logActivity(`Camera hardware failure during retake: ${errorMessage}`, {
          error: errorMessage,
          photoType: currentPhotoType,
          level: 'error',
          recovery: true
        })
        await new Promise((resolve) => setTimeout(resolve, 500))
        void handleCameraRecovery()
        return
      }

      logActivity(`Retake capture failed: ${errorMessage}`, {
        error: errorMessage,
        level: 'error',
        photoType: currentPhotoType
      })
      setIsCaptureError(true)
    } finally {
      captureLockRef.current = false
    }
  }

  handleRetakeCaptureRef.current = handleRetakeCapture

  // Frame-based mode: Handle continue - navigate to Filter if applyAfterCapture, otherwise Select
  const handleFrameBasedContinue = (): void => {
    if (!isMountedRef.current) return
    void (async (): Promise<void> => {
      await stopSessionRecordingAndUpload()
      if (!isMountedRef.current) return
      stateUploader.uploadSessionState().catch(() => {})

      const applyAfterCapture = filterSettings?.applyAfterCapture || false
      const filterEnabled = filterSettings?.enabled

      if (applyAfterCapture && filterEnabled) {
        navigate(`/${projectId}/filter`)
        return
      }
      navigate(`/${projectId}/select`)
    })()
  }

  const handlePhotoClick = (id: string): void => {
    setSelectedPhotos((prev) => {
      if (prev.includes(id)) {
        return prev.filter((i) => i !== id)
      }
      // If maxPhotos is null/undefined, allow unlimited selections
      if (maxPhotos === null || maxPhotos === undefined || prev.length < maxPhotos) {
        return [...prev, id]
      }
      return prev
    })
  }

  const handleRetake = async (): Promise<void> => {
    // Log processing start
    logActivity(`Selected ${selectedPhotos.length} photos to retake`, {
      selectedCount: selectedPhotos.length,
      selectedPhotoIds: selectedPhotos
    })

    try {
      // Filter out selected items and their URLs
      const remainingItems = gallery.capturedItems.filter(
        (item) => !selectedPhotos.includes(item.id)
      )

      // Clean up URLs and delete selected photos
      const itemsToDelete = gallery.capturedItems.filter((item) => selectedPhotos.includes(item.id))
      itemsToDelete.forEach((item) => {
        if (item.photoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.photoUrl)
        }
        if (item.videoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.videoUrl)
        }
        if (item.recordingUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(item.recordingUrl)
        }
      })

      // Update session state
      setSessionState({
        userPreferences: {
          retakeCount: selectedPhotos.length,
          retakeSessionsCompleted: (userPreferences.retakeSessionsCompleted || 0) + 1
        },
        gallery: {
          capturedItems: remainingItems
        }
      })

      await stopSessionRecordingAndUpload()

      // Upload session state before navigating (fire-and-forget)
      stateUploader.uploadSessionState().catch(() => {})
      if (isMountedRef.current) {
        navigate(`/${projectId}/capture`)
      }
    } catch (error) {
      console.error('Error in retake process:', error)

      // Log processing failure
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Retake processing failed: ${errorMessage}`, {
        error: errorMessage,
        selectedCount: selectedPhotos.length,
        level: 'error'
      })
    }
  }

  const handleContinue = (): void => {
    if (!isMountedRef.current) return

    // Frame-based mode: check filter settings first
    if (captureMode === 'frame-based') {
      void (async (): Promise<void> => {
        await stopSessionRecordingAndUpload()
        if (!isMountedRef.current) return
        const applyAfterCapture = filterSettings?.applyAfterCapture || false
        const filterEnabled = filterSettings?.enabled

        if (applyAfterCapture && filterEnabled) {
          navigate(`/${projectId}/filter`)
          return
        }
        navigate(`/${projectId}/select`)
      })()
      return
    }

    // Standard mode: check filter settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    const filterEnabled = filterSettings?.enabled

    // If applyAfterCapture is enabled and filter is enabled, navigate to filter
    if (applyAfterCapture && filterEnabled) {
      navigate(`/${projectId}/filter`)
    } else {
      // Always navigate to select page
      navigate(`/${projectId}/select`)
    }
  }

  const handleKeepAllClick = (): void => {
    // Only show confirmation when the Keep All button is actually enabled
    if (selectedPhotos.length === 0) {
      setShowConfirmModal(true)
    }
  }

  const handleConfirmKeepAll = (): void => {
    if (isProcessingConfirm) return

    setIsProcessingConfirm(true)
    try {
      handleContinue()
    } finally {
      // Page will usually navigate away, but keep local state consistent
      setIsProcessingConfirm(false)
      setShowConfirmModal(false)
    }
  }

  // Frame-based mode UI
  if (captureMode === 'frame-based') {
    // Navigate to frame selection if no frame selected
    if (!selectedFrame) {
      navigate(`/${projectId}/frame`)
      return null
    }
    
    // Calculate scale for fullscreen frame display
    const screenWidth = layoutCanvasW
    const screenHeight = layoutCanvasH
    const frameWidth = selectedFrame.drawParams.canvasWidth
    const frameHeight = selectedFrame.drawParams.canvasHeight
    const isLandscape = screenWidth > screenHeight

    // Scale frame to fit screen while maintaining aspect ratio
    const frameChromePortraitTight = !isLandscape && (retakeCompact || retakeTiny)
    const frameSessionTimerCompact = retakeCompact || retakeTiny
    const fbScale = retakeCompact ? 0.62 : 1
    const paddingSize = frameChromePortraitTight
      ? Math.max(screenWidth * 0.02, 10)
      : Math.max(screenWidth * 0.015 * fbScale, retakeCompact ? 12 : 16)
    const topGap = frameChromePortraitTight
      ? Math.max(screenHeight * 0.032, 32)
      : Math.max(screenHeight * 0.05 * fbScale, retakeCompact ? 48 : 80)
    const buttonAreaHeight = frameChromePortraitTight
      ? Math.max(screenHeight * 0.075, 62)
      : Math.max(screenHeight * 0.15 * fbScale, retakeCompact ? 100 : 180)
    const availableWidth = screenWidth - paddingSize * 2
    const availableHeight = screenHeight - paddingSize * 2 - topGap - buttonAreaHeight
    const scale = Math.min(availableWidth / frameWidth, availableHeight / frameHeight)

    const countdownTop = Math.max(screenHeight * 0.02, 32)
    const countdownRight = Math.max(screenWidth * 0.02, 32)
    const countdownFontSize = Math.min(Math.max(screenHeight * 0.022, 22), 34)
    const fbFooterBtnPadding = frameSessionTimerCompact ? '!px-3 !py-2 !text-sm' : '!px-5 !py-2.5'
    const fbMirrorBtnClass = frameSessionTimerCompact
      ? frameChromePortraitTight
        ? 'gap-1.5 px-3 py-2 text-sm'
        : 'gap-2 px-4 py-2 text-sm'
      : 'gap-3 px-6 py-3'
    const fbFooterFontPx = frameSessionTimerCompact ? 14 : Math.max(screenHeight * 0.018, 18)
    const fbFooterRowGap = frameChromePortraitTight ? 'gap-2' : frameSessionTimerCompact ? 'gap-3' : 'gap-4'

    // Main left preview: match FrameBasedCapture — camera aspect (e.g. 4:3) with frame-slot highlight (e.g. 1:1). fillContainer disables overlay in Preview.
    const slotForMainPreview =
      retakingPhotoType != null
        ? frameBasedPhotoSlots.find((s) => s.type === retakingPhotoType)
        : null
    const mainPreviewHighlightRatio = slotForMainPreview
      ? getPhotoRatio(slotForMainPreview)
      : userPreferences.framePhotoRatio ?? getNormalizedCaptureRatio() ?? undefined
    const cameraPreviewRatio = getNormalizedCaptureRatio()

    const frameConfig = {
      id: selectedFrame._id,
      baseImage: selectedFrame.thumbnail,
      drawParams: selectedFrame.drawParams
    }
    const selectedPhotosForPreview = frameBasedUniquePhotoTypesArray
      .map((photoType) => {
        const url = capturedPhotosByType.get(photoType) || ''
        if (!url) return ''
        return mirroredPhotoTypes.has(photoType) ? `mirror:${url}` : url
      })
      .filter(Boolean)

    // Determine button text based on enabled pages
    const editSettings = globalConfig?.pages.edit?.settings
    const selectSettings = globalConfig?.pages.select?.settings
    const editEnabled = editSettings?.enabled ?? false
    const selectEnabled = selectSettings?.enabled ?? false
    // Digital web booths never print — label the primary action "Continue", not
    // "Print", and the downstream print step is skipped (see Select.tsx).
    const continueButtonText = editEnabled || selectEnabled || isDigitalMode()
      ? t('common.continue', 'Continue')
      : t('common.print', 'Print')
    const shouldShowFrameRetakeButtons =
      retakingPhotoType === null && !capturing && !isCapturingWithShutter && !isRecoveringCamera

    return (
      <div className="relative w-full h-full bg-black">
        <div id="flash" className="w-full h-full flash"></div>

        {isCapturingWithShutter && (
          <div className="absolute inset-0 bg-white/50 z-[5] flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-black mb-4">
                {'\u{1F4F8}'} {t('capture.lookAtCamera', 'Look at the Camera')}
              </h2>
              <p className="text-xl text-black/80">
                {t('capture.stayStill', 'Please stay still while we capture your photo...')}
              </p>
            </div>
          </div>
        )}

        {/* Shared session timer — compact chip on small viewports */}
        <div
          className={`absolute z-50 ${frameSessionTimerCompact ? 'right-3 top-3 sm:right-4 sm:top-4' : ''}`}
          style={frameSessionTimerCompact ? undefined : { top: `${countdownTop}px`, right: `${countdownRight}px` }}
        >
          {frameSessionTimerCompact ? (
            <div className="rounded-lg border border-white/35 bg-black/60 px-2.5 py-1.5 text-center shadow-lg backdrop-blur-sm">
              <div className="text-[10px] font-medium uppercase tracking-wide text-white/75">
                {t('capture.timeRemaining', 'Time remaining')}
              </div>
              <div className="font-mono text-base font-semibold tabular-nums leading-none text-white drop-shadow-md">
                {sharedRemainingTime}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border-2 border-white/90 bg-black/40 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4">
              <div className="mb-1 text-sm text-white/90">{t('capture.timeRemaining', 'Time remaining')}</div>
              <div
                className="font-bold font-mono tabular-nums text-white"
                style={{ fontSize: `${countdownFontSize}px` }}
              >
                {sharedRemainingTime}
              </div>
            </div>
          )}
        </div>

        {isLandscape ? (
          // Landscape Mode: Left preview area + Right frame strip
          <div className="w-full h-full flex">
            {/* Left Area - Big Camera Preview */}
            <div className="w-2/3 relative">
              {/* Capture Countdown Overlay */}
              {capturing && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <span
                    key={`countdown-${countdownKey}-${captureCountdown}`}
                    className="text-[200px] text-white animate-countdown drop-shadow-lg"
                  >
                    {captureCountdown}
                  </span>
                </div>
              )}

              {/* Capture Error Message */}
              {isCaptureError && (
                <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
                  <div className="text-center">
                    <h2 className="text-4xl font-bold text-red-500 mb-4">
                      {t('capture.captureFailed', 'Capture Failed')}
                    </h2>
                    <p className="text-xl text-white/80 mb-6">
                      {t('capture.stayStillCapturing', 'Please stay still when capturing')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCaptureError(false)
                        if (retakingPhotoType !== null) {
                          handleRetakePhoto(retakingPhotoType)
                        }
                      }}
                      className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3 text-white rounded-full"
                      style={{
                        fontSize: `${Math.max(screenHeight * 0.018, 18)}px`
                      }}
                    >
                      {t('common.retry', 'Retry')}
                    </button>
                  </div>
                </div>
              )}

              {/* Camera Preview — no fillContainer so 4:3 vs 1:1 (etc.) mask matches FrameBasedCapture */}
              <div className="w-full h-full">
                <Preview
                  key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${mainPreviewHighlightRatio ?? 'auto'}-${retakingPhotoType ?? 'idle'}`}
                  adjustments={filterAdjustments}
                  ratio={mainPreviewHighlightRatio}
                  cameraRatio={cameraPreviewRatio}
                  cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  filter={selectedFilter?._id}
                  postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                  onCanvasReady={setCanvasDimensions}
                  onRecordingCanvasReady={onRecordingCanvasReady}
                  isPaused={isPreviewPaused}
                  captureShareRole="producer"
                  captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                />
              </div>
            </div>

            {/* Right Area - Frame Strip */}
            <div className="w-1/3 relative bg-black">
              {/* Frame Strip */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{ padding: `${paddingSize}px` }}
              >
                <div
                  className="relative flex items-center justify-center"
                  style={{
                    width: `${frameWidth * scale * 0.95}px`,
                    height: `${frameHeight * scale * 0.95}px`
                  }}
                >
                  {retakingPhotoType === null && !capturing ? (
                    <div className="absolute inset-0">
                      <PrintPreview
                        frameConfig={frameConfig}
                        selectedPhotos={selectedPhotosForPreview}
                        isInteractive={true}
                        sessionId={id || ''}
                      />

                      {/* Overlay individual retake buttons on top of the interactive preview */}
                      {frameBasedPhotoSlots.map((slot, index) => {
                        const capturedPhotoUrl = capturedPhotosByType.get(slot.type)
                        const isFirstSlotOfType =
                          frameBasedPhotoSlots.findIndex((s) => s.type === slot.type) === index

                        if (!capturedPhotoUrl || !isFirstSlotOfType) return null

                        return (
                          <button
                            key={`retake-btn-${slot.type}-${index}`}
                            type="button"
                            onClick={() => handleRetakePhoto(slot.type)}
                            disabled={isSharedCountdownExpired}
                            className={`absolute flex items-center justify-center rounded-full bg-black/50 shadow-lg transition-all hover:opacity-90 z-10 ${retakeFabClass}`}
                            style={{
                              left: `${slot.left * scale * 0.95 + slot.width * scale * 0.95 - (retakeTiny ? 40 : retakeCompact ? 48 : 56)}px`,
                              top: `${slot.top * scale * 0.95 + 8}px`,
                              color: '#FFFFFF'
                            }}
                            aria-label={`Retake photo ${slot.type}`}
                          >
                            <FaUndo style={{ fontSize: retakeTiny ? '1rem' : '1.25rem' }} />
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <FrameOrderedLiveLayers
                      drawParamsImages={selectedFrame.drawParams.images}
                      thumbnailUrl={selectedFrame.thumbnail}
                      thumbnailSource={selectedFrame.thumbnailSource}
                      frameBackgroundColor={selectedFrame.drawParams.backgroundColor || '#FFFFFF'}
                      scale={scale}
                      scaleMultiplier={0.95}
                      uniquePhotoTypesArray={frameBasedUniquePhotoTypesArray}
                      capturedPhotosByType={capturedPhotosByType}
                      filterAdjustments={filterAdjustments}
                      selectedFilterId={selectedFilter?._id}
                      postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                      cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                      getPhotoRatio={getPhotoRatio}
                      captureFlow={false}
                      currentPhotoTypeIndex={0}
                      retakingPhotoType={retakingPhotoType}
                      showRetakeButtons={shouldShowFrameRetakeButtons}
                      onRetakePhoto={handleRetakePhoto}
                      isSharedCountdownExpired={isSharedCountdownExpired}
                      capturing={capturing}
                      livePreviewPaused={isPreviewPaused}
                      cameraRatio={cameraPreviewRatio}
                      captureShareRole="consumer"
                      captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                    />
                  )}
                </div>
              </div>

              {/* Button Area */}
              <div
                className={`absolute bottom-0 left-0 right-0 z-10 flex items-center justify-center px-2 ${fbFooterRowGap}`}
                style={{ height: `${buttonAreaHeight}px` }}
              >
                {retakingPhotoType === null && !capturing && selectedPhotosForPreview.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const willMirrorAll = frameBasedUniquePhotoTypesArray.some(
                        (photoType) => !mirroredPhotoTypes.has(photoType)
                      )
                      const next = new Set(mirroredPhotoTypes)
                      frameBasedUniquePhotoTypesArray.forEach((photoType) => {
                        if (willMirrorAll) next.add(photoType)
                        else next.delete(photoType)
                      })
                      setIsAllMirrored(willMirrorAll)
                      setMirroredPhotoTypes(next)
                    }}
                    className={`bg-white/10 hover:bg-white/20 font-medium transition-all duration-200 cursor-pointer text-white rounded-full flex items-center shadow-md ${
                      frameSessionTimerCompact ? 'border border-white/35' : 'border-2 border-white/30'
                    } ${fbMirrorBtnClass}`}
                    style={{
                      fontSize: `${fbFooterFontPx}px`,
                      opacity: isAllMirrored ? 1 : 0.9
                    }}
                  >
                    <FaExchangeAlt className={frameSessionTimerCompact ? 'h-3.5 w-3.5' : ''} />
                    <span>{t('select.mirror', 'Mirror')}</span>
                  </button>
                )}
                <Button
                  text={continueButtonText}
                  onClick={handleFrameBasedContinue}
                  disabled={isSharedCountdownExpired}
                  variant="primary"
                  className={fbFooterBtnPadding}
                  style={{
                    fontSize: `${fbFooterFontPx}px`
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          // Portrait Mode: Centered frame strip only
          <div
            className="relative w-full h-full flex flex-col overflow-hidden"
            style={{ padding: `${paddingSize}px` }}
          >
            {/* Frame Preview Area */}
            <div
              className="flex-1 min-h-0 relative flex items-center justify-center"
              style={{ paddingTop: `${topGap}px` }}
            >
              <div className="absolute inset-0 z-0 opacity-0 pointer-events-none" aria-hidden>
                <Preview
                  key={`${selectedFilter?._id}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${getNormalizedCaptureRatio()}-${mainPreviewHighlightRatio ?? 'auto'}-${retakingPhotoType ?? 'idle'}`}
                  adjustments={filterAdjustments}
                  ratio={mainPreviewHighlightRatio}
                  cameraRatio={cameraPreviewRatio}
                  cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  filter={selectedFilter?._id}
                  postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                  onCanvasReady={setCanvasDimensions}
                  onRecordingCanvasReady={onRecordingCanvasReady}
                  isPaused={isPreviewPaused}
                  captureShareRole="producer"
                  captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                />
              </div>
              <div
                className="absolute z-[1] flex items-center justify-center"
                style={{
                  width: `${frameWidth * scale}px`,
                  height: `${frameHeight * scale}px`
                }}
              >
                {retakingPhotoType === null && !capturing ? (
                  <div className="absolute inset-0">
                    <PrintPreview
                      frameConfig={frameConfig}
                      selectedPhotos={selectedPhotosForPreview}
                      isInteractive={true}
                      sessionId={id || ''}
                    />

                    {/* Overlay individual retake buttons on top of the interactive preview */}
                    {frameBasedPhotoSlots.map((slot, index) => {
                      const capturedPhotoUrl = capturedPhotosByType.get(slot.type)
                      const isFirstSlotOfType =
                        frameBasedPhotoSlots.findIndex((s) => s.type === slot.type) === index

                      if (!capturedPhotoUrl || !isFirstSlotOfType) return null

                      return (
                        <button
                          key={`retake-btn-${slot.type}-${index}`}
                          type="button"
                          onClick={() => handleRetakePhoto(slot.type)}
                          disabled={isSharedCountdownExpired}
                          className={`absolute flex items-center justify-center rounded-full bg-black/50 shadow-lg transition-all hover:opacity-90 z-10 ${retakeFabClass}`}
                          style={{
                            left: `${slot.left * scale + slot.width * scale - (retakeTiny ? 40 : retakeCompact ? 48 : 56)}px`,
                            top: `${slot.top * scale + 8}px`,
                            color: '#FFFFFF'
                          }}
                          aria-label={`Retake photo ${slot.type}`}
                        >
                          <FaUndo style={{ fontSize: retakeTiny ? '1rem' : '1.25rem' }} />
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <FrameOrderedLiveLayers
                    drawParamsImages={selectedFrame.drawParams.images}
                    thumbnailUrl={selectedFrame.thumbnail}
                    thumbnailSource={selectedFrame.thumbnailSource}
                    frameBackgroundColor={selectedFrame.drawParams.backgroundColor || '#FFFFFF'}
                    scale={scale}
                    uniquePhotoTypesArray={frameBasedUniquePhotoTypesArray}
                    capturedPhotosByType={capturedPhotosByType}
                    filterAdjustments={filterAdjustments}
                    selectedFilterId={selectedFilter?._id}
                    postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                    cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                    getPhotoRatio={getPhotoRatio}
                    captureFlow={false}
                    currentPhotoTypeIndex={0}
                    retakingPhotoType={retakingPhotoType}
                    showRetakeButtons={shouldShowFrameRetakeButtons}
                    onRetakePhoto={handleRetakePhoto}
                    isSharedCountdownExpired={isSharedCountdownExpired}
                    capturing={capturing}
                    livePreviewPaused={isPreviewPaused}
                    cameraRatio={cameraPreviewRatio}
                    captureShareRole="consumer"
                    captureShareGroupId={PREVIEW_CAPTURE_SHARE_FRAME_STRIP}
                  />
                )}
              </div>
            </div>

        {/* Capture Countdown Overlay */}
        {capturing && (
          <div className="absolute inset-0 z-[4] flex items-center justify-center">
            <span
              key={`countdown-${countdownKey}-${captureCountdown}`}
              className="text-white animate-countdown"
              style={{
                fontSize: `${frameChromePortraitTight ? Math.min(Math.max(screenHeight * 0.12, 64), 120) : Math.max(screenHeight * 0.18, 200)}px`
              }}
            >
              {captureCountdown}
            </span>
          </div>
        )}

        {/* Capture Error Message */}
        {isCaptureError && (
          <div className="absolute inset-0 bg-black/80 z-[5] flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-4xl font-bold text-red-500 mb-4">
                {t('capture.captureFailed', 'Capture Failed')}
              </h2>
              <p className="text-xl text-white/80 mb-6">
                {t('capture.stayStillCapturing', 'Please stay still when capturing')}
              </p>
              <button
                type="button"
                onClick={() => {
                  setIsCaptureError(false)
                  if (retakingPhotoType !== null) {
                    handleRetakePhoto(retakingPhotoType)
                  }
                }}
                className="bg-white/10 hover:bg-white/20 border-2 border-white/30 font-medium transition-all duration-200 cursor-pointer px-6 py-3"
                style={{
                  fontSize: `${Math.max(screenHeight * 0.018, 18)}px`,
                  color: '#ffffff'
                }}
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          </div>
        )}

            {/* Button Area */}
            <div
              className="flex-shrink-0 relative flex items-center justify-center px-2"
              style={{ height: `${buttonAreaHeight}px` }}
            >
              <div className={`z-10 flex flex-wrap items-center justify-center ${fbFooterRowGap}`}>
                {retakingPhotoType === null && !capturing && selectedPhotosForPreview.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const willMirrorAll = frameBasedUniquePhotoTypesArray.some(
                        (photoType) => !mirroredPhotoTypes.has(photoType)
                      )
                      const next = new Set(mirroredPhotoTypes)
                      frameBasedUniquePhotoTypesArray.forEach((photoType) => {
                        if (willMirrorAll) next.add(photoType)
                        else next.delete(photoType)
                      })
                      setIsAllMirrored(willMirrorAll)
                      setMirroredPhotoTypes(next)
                    }}
                    className={`bg-white/10 hover:bg-white/20 font-medium transition-all duration-200 cursor-pointer text-white rounded-full flex items-center shadow-md ${
                      frameSessionTimerCompact ? 'border border-white/35' : 'border-2 border-white/30'
                    } ${fbMirrorBtnClass}`}
                    style={{
                      fontSize: `${fbFooterFontPx}px`,
                      opacity: isAllMirrored ? 1 : 0.9
                    }}
                  >
                    <FaExchangeAlt className={frameSessionTimerCompact ? 'h-3.5 w-3.5' : ''} />
                    <span>{t('select.mirror', 'Mirror')}</span>
                  </button>
                )}
                <Button
                  text={continueButtonText}
                  onClick={handleFrameBasedContinue}
                  disabled={isSharedCountdownExpired}
                  variant="primary"
                  className={fbFooterBtnPadding}
                  style={{
                    fontSize: `${fbFooterFontPx}px`
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Standard mode UI (existing)
  const retakeFooterTimerClass = retakeTiny
    ? 'text-xs font-mono tabular-nums text-plaster/80'
    : retakeCompact
      ? 'text-sm font-mono tabular-nums text-plaster/80'
      : 'text-base font-mono tabular-nums text-plaster/90 sm:text-lg'

  return (
    <div className="relative flex h-full w-full min-h-0 flex-col bg-black">
      {/* Title + photo grid travel together as ONE vertically-centered cluster
          (owner review: a top-pinned title with the grid floating mid-canvas read
          as three disconnected bands). Centered while it fits, top-aligned +
          scrollable only on genuine overflow; footer stays a pinned bar. */}
      <div
        ref={retakeAreaRef}
        className={`relative flex min-h-0 pt-3 min-w-0 flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar-dark ${
          retakeTiny
            ? 'px-3 pb-3 pt-1'
            : retakeCompact
              ? 'px-3 pb-3'
              : 'px-16 pb-12'
        }`}
      >
        <div className="mx-auto my-auto flex w-full min-w-0 flex-col">
      {/* Header: landscape desktop keeps only pill up top; headline sits with centered grid */}
      <div
        ref={retakeHeaderRef}
        className={`shrink-0 flex flex-col items-center ${
          retakeTiny ? 'mb-3 px-3' : retakeCompact ? 'mb-4 px-4' : 'mb-8 px-8'
        }`}
      >
        <div
          className={`flex flex-wrap items-center justify-center ${
            retakeCompact ? 'gap-3' : isPortrait ? 'gap-5' : 'gap-3'
          }`}
        >
          <h1
            className={`text-center font-bold text-white ${
              retakeTiny
                ? 'text-lg leading-tight'
                : retakeCompact
                  ? 'text-2xl'
                  : isPortrait
                    ? 'text-4xl leading-tight tracking-tight'
                    : isLandscape && !retakeCompact
                      ? 'text-4xl leading-tight tracking-tight'
                      : 'text-3xl'
            }`}
          >
            {t('retake.headline', 'Tap photos to retake')}
          </h1>
          {maxPhotos != null && maxPhotos > 0 && (
            <span
              className={
                retakeTiny
                  ? 'text-sm text-plaster/60'
                  : retakeCompact
                    ? 'text-base text-plaster/60'
                    : isPortrait
                      ? 'text-xl text-plaster/65'
                      : 'text-lg text-plaster/60'
              }
            >
              {t('retake.maxHint', 'Max {{count}}', { count: maxPhotos })}
            </span>
          )}
        </div>
        {typeof remainingRetakeSessions === 'number' && maxRetakeSession != null && (
          <p
            className={`mt-2 max-w-full text-center font-medium tabular-nums tracking-tight text-plaster/90 ${
              retakeTiny
                ? 'text-[10px] leading-snug'
                : retakeCompact
                  ? 'text-xs leading-snug'
                  : 'text-sm leading-normal'
            }`}
            role="status"
            aria-live="polite"
          >
            {t('retake.pillSessions', '{{count}} left · Session {{current}} of {{max}}', {
              count: remainingRetakeSessions,
              current: retakeSessionCount + 1,
              max: maxRetakeSession
            })}
          </p>
        )}
      </div>

        <div
          className="mx-auto w-full min-w-0"
          style={retakeGridMaxWidth ? { maxWidth: retakeGridMaxWidth } : undefined}
        >
        <div
          className={`mx-auto grid w-full ${retakeGridGap.cls} ${retakeGridLayout.cols} ${retakeGridLayout.wrap} ${
            isLandscape && !retakeCompact ? 'items-stretch' : ''
          }`}
          role="listbox"
          aria-label={t('retake.headline', 'Tap photos to retake')}
          aria-multiselectable="true"
        >
          {retakeStandardPhotos.map((item, index) => {
              const isSelected = selectedPhotos.includes(item.id)

              return (
                <button
                  role="option"
                  type="button"
                  key={item.id}
                  onClick={() => handlePhotoClick(item.id)}
                  className={`
                  group relative overflow-hidden rounded-xl transition-all duration-300
                  ${retakeGridLayout.cell}
                  ${isSelected ? 'ring-4 ring-red-500 scale-95 grayscale' : 'hover:scale-[0.98]'}
                `}
                  style={{
                    boxShadow: isSelected ? `0 0 0 4px #EF4444` : ''
                  }}
                >
                  <span className="sr-only">
                    {isSelected
                      ? t('retake.photoSelectedAlt', 'Photo {{number}} selected for retake', { number: index + 1 })
                      : t('retake.photoAlt', 'Retake Photo {{number}}', { number: index + 1 })}
                  </span>
                  {item.photoUrl && (
                    <>
                      <PhotoFrame
                        src={item.photoUrl}
                        alt={t('retake.photoAlt', 'Photo {{number}}', { number: index + 1 })}
                        className="w-full"
                        displayRatio={userPreferences.framePhotoRatio || 1}
                      />
                      {isSelected && (
                        <div
                          className={`absolute top-2 right-2 z-10 flex items-center justify-center rounded-full bg-black/50 text-white ${retakeSelectedBadgeClass}`}
                          aria-hidden
                        >
                          <FaUndo className="text-lg" />
                        </div>
                      )}
                      <div
                        className={`
                      absolute inset-0 flex items-center justify-center
                      bg-black/50 transition-opacity duration-300
                      ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                    `}
                      >
                        <p className={`${retakeTiny ? 'text-sm' : 'text-lg'} font-medium text-white`}>
                          {isSelected
                            ? t('retake.overlaySelected', 'Selected')
                            : t('retake.overlayRetake', 'Retake')}
                        </p>
                      </div>
                    </>
                  )}
                </button>
              )
            })}
        </div>
        </div>
        </div>
      </div>

      {/* Footer: Retake left; timer + Keep all grouped on the right */}
      <div
        className={`shrink-0 flex min-w-0 flex-nowrap items-center justify-between ${
          retakeTiny ? 'gap-1.5 px-2 py-2' : retakeCompact ? 'gap-2 px-3 py-3' : isPortrait ? 'gap-4 px-12 py-8' : 'gap-4 px-8 py-6'
        }`}
      >
        <Button
          text={
            <>
              <FaUndoAlt
                className={`inline-block ${retakeTiny ? 'mr-1 text-xs' : retakeCompact ? 'mr-1 text-sm' : 'mr-2 text-lg'}`}
              />
              {t('retake.retakeCount', 'Retake ({{count}})', { count: selectedPhotos.length })}
            </>
          }
          onClick={handleRetake}
          data-nav-to="capture"
          disabled={selectedPhotos.length === 0}
          className={`flex shrink-0 items-center gap-1 ${retakeTiny ? '!px-2 !py-1.5 !text-xs' : retakeCompact ? '!px-3 !py-2 !text-sm' : '!min-h-[64px] !px-10 !text-xl'}`}
          variant={selectedPhotos.length > 0 ? 'primary' : 'secondary'}
        />
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <span
            className={`shrink-0 ${retakeFooterTimerClass} ${
              isCountdownUrgent(remainingTime) ? '!font-bold !text-red-500' : ''
            }`}
          >
            {remainingTime}
          </span>
          <Button
            text={t('retake.keepAll', 'Keep all')}
            onClick={handleKeepAllClick}
            data-nav-to="select"
            disabled={selectedPhotos.length > 0}
            variant={selectedPhotos.length === 0 ? 'primary' : 'secondary'}
            className={`shrink-0 whitespace-nowrap ${retakeTiny ? '!px-2 !py-1.5 !text-xs' : retakeCompact ? '!px-3 !py-2 !text-sm' : '!min-h-[64px] !px-10 !text-xl'}`}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              {t('retake.confirmContinue', 'Continue with all photos?')}
            </h2>
            <p className="text-gray-700 text-base mb-6 leading-relaxed">
              {t('retake.confirmContinueHint', 'You can go back to retake any photo, or continue to the next step.')}
            </p>
            <div className="flex justify-end gap-3 flex-wrap">
              <Button
                text={t('retake.back', 'Back')}
                onClick={() => setShowConfirmModal(false)}
                variant="secondary"
                className="!px-5 !py-2 !text-base"
              />
              <Button
                text={isProcessingConfirm ? t('retake.confirmingKeepAll', 'Continuing...') : t('retake.continue', 'Continue')}
                onClick={handleConfirmKeepAll}
                disabled={isProcessingConfirm}
                variant="primary"
                className="!px-5 !py-2 !text-base"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RetakePage
